import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Sentry from '@sentry/nextjs';
import { useRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';

import StandardModal from '@/components/commons/StandardModal';
import {
    UserContent,
    onboardingAtom,
    userAtom,
} from '@/components/with_auth/State';
import Title from '@/components/commons/page-elements/Title';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import { UPDATE_USER } from '@/components/onboarding/Actions';
import Container from '@/components/commons/page-elements/Container';
import Checkbox from '@/components/forms/checkbox';
import FormError from '@/components/forms/FormError';
import WarningCard from '@/components/commons/warning_card';

import styles from './MonitoredLabels.module.css';

function OnboardingFoldersModal() {
    const [open, setOpen] = useState(true);
    const [user, setUserRecoil] = useRecoilState(userAtom);
    const [onboardingData, setOnboardingData] = useRecoilState(onboardingAtom);
    const [formError, setFormError] = useState('');

    const [updateUser] = useMutation(UPDATE_USER);

    const handleFormSubmit = async (values, { setStatus, setSubmitting }) => {
        try {
            const slowArchivalPeriod = parseInt(values.slowArchivalPeriod, 10);
            const fastArchivalPeriod = parseInt(values.fastArchivalPeriod, 10);
            if (slowArchivalPeriod
                && (!fastArchivalPeriod
                    || Number.isNaN(fastArchivalPeriod)
                    || fastArchivalPeriod < 1)) {
                setSubmitting(false);
                if (formError !== '') setFormError('');
                setStatus({ errors: { fastArchivalPeriod: ['A valid number above 0 is required when Slow Archival Period is provided.'] } });
                return;
            }
            if (fastArchivalPeriod
                && (!slowArchivalPeriod
                    || Number.isNaN(slowArchivalPeriod)
                    || slowArchivalPeriod < 1)) {
                setSubmitting(false);
                if (formError !== '') setFormError('');
                setStatus({ errors: { slowArchivalPeriod: ['A valid number above 0 is required when Fast Archival Period is provided.'] } });
                return;
            }

            const enableArchival = Boolean(slowArchivalPeriod) && Boolean(fastArchivalPeriod);
            const attributes: Partial<UserContent> = {
                enableArchival,
                fastArchivalPeriod,
                slowArchivalPeriod,
                usesSuperhuman: values.usesSuperhuman,
                usesSanebox: values.usesSanebox,
            };
            const response = await updateUser({
                variables: {
                    updateUserInput: {
                        attributes,
                    },
                },
            });
            // Do we care if this response failed to show errors? The users can't
            // action anything and they wouldn't know why it was failing in the backend.
            if (response.data.updateUser.user) {
                const { user: newUser } = response.data.updateUser;
                // update recoil so user doesn't see this page again if they navigate
                // away and back without a hard refresh
                setUserRecoil(newUser);
            }
        } catch (err) {
            const msg = 'An unexpected error occurred. Failed to save.';
            setFormError(msg);
            Sentry.captureException(err);
        }
        setOpen(false);
        setOnboardingData({
            ...onboardingData,
            folders: 2,
        });
    };

    return (
        <StandardModal
            open={open}
            onClose={() => {
                setOpen(false);
                setOnboardingData({
                    ...onboardingData,
                    folders: 2,
                });
            }}
            classNames={{
                modal: 'p-6 sm:p-10 max-w-none sm:max-w-[1175px] max-h-[90vh]',
                closeButton: 'modal--closeButton',
            }}
        >
            {
                formError !== '' && (
                    <div className="w-full my-[20px]">
                        <WarningCard message={formError} />
                    </div>
                )
            }

            <Formik
                onSubmit={handleFormSubmit}
                initialValues={{
                    usesSuperhuman: Boolean(user?.usesSuperhuman),
                    usesSanebox: Boolean(user?.usesSanebox),
                    slowArchivalPeriod: user.slowArchivalPeriod || 21,
                    fastArchivalPeriod: user.fastArchivalPeriod || 7,
                }}
            >
                {({ status, isSubmitting }) => (
                    <Form id="onboarding-folder-modal">
                        <div className="flex flex-col sm:flex-row justify-between items-center">
                            <div className="w-none sm:w-[500px]">
                                <Title title="What are Smart Folders?" subtitle="We understand that letting machines manage your email can be a scary proposition. Our smart approach makes getting started simple." />
                            </div>

                            <Button
                                type={ButtonType.submit}
                                size={ButtonSize.small}
                                text="Create Smart Folders"
                                disabled={isSubmitting}
                                externalStyles="mt-6 sm:mt-0 w-[210px] block border-gray-300 border-y border-x h-10 py-0 px-0 "
                                form="onboarding-folder-modal"
                            />
                        </div>
                        <div className="flex flex-col sm:flex-row justify-between mt-5">
                            <div className="mb-4 sm:mb-0 max-w-[369px]">
                                <img src="/images/smart-folder-labels.png" alt="Example Smart Folders" />
                            </div>
                            <div className="w-none sm:w-[625px] ml-0 sm:ml-10">
                                <h4 className="font-semibold text-[20px] text-cleanSlate">
                                    1. We&apos;ll create a few folders.
                                </h4>
                                <p className="leading-normal my-2">
                                    We&apos;ll create Smart Folders in your email account.
                                    These folders will appear alongside any other folders
                                    and labels you already use.
                                </p>
                                <Container externalStyles="mb-2 mt-6 sm:mt-0">
                                    <Checkbox
                                        name="usesSuperhuman"
                                        label="I'm a Superhuman user"
                                        description="Sixty will label your emails, but will not move them for you."
                                    />
                                </Container>
                                <Container externalStyles="mb-7 mt-6 sm:mt-0">
                                    <Checkbox
                                        name="usesSanebox"
                                        label="I'm a Sanebox user"
                                        description="Sixty will use your existing folders rather than creating new ones."
                                    />
                                </Container>

                                <h4 className="font-semibold text-[20px] text-cleanSlate">
                                    2. We&apos;ll analyze and move emails for you.
                                </h4>
                                <p className="leading-normal my-2">
                                    When an email arrives from someone you don&apos;t know,
                                    we&apos;ll move it into the proper Smart Folder.
                                </p>
                                <Container externalStyles="mb-7 mt-6 sm:mt-0">
                                    <p className="text-base font-bold mb-2">Archival preferences</p>
                                    <p className="leading-loose text-sm font-medium mb-0 ">
                                        We&apos;ll also help you keep things tidy
                                        by moving mail to the archive after a cooling-off
                                        period. Tell us how often you&apos;d like that to happen.
                                        <br />
                                        <br />
                                    </p>

                                    <div className="flex flex-col md:flex-row">
                                        <p className="leading-loose text-sm font-medium  mr-2">
                                            Folders I designate as &quot;slow archival&quot;
                                            should archive messages after
                                            {' '}
                                            <Field
                                                type="number"
                                                name="slowArchivalPeriod"
                                                placeholder="21"
                                                className={styles.inlineInput}
                                            />
                                            {' '}
                                            days.
                                        </p>
                                    </div>
                                    <FormError error={status?.errors?.slowArchivalPeriod} />
                                    <div className="flex flex-col md:flex-row">
                                        <p className="text-sm font-medium leading-8 mr-2">
                                            Folders I designate as &quot;fast archival&quot;
                                            should archive messages after
                                            {' '}
                                            <Field
                                                type="number"
                                                name="fastArchivalPeriod"
                                                placeholder="7"
                                                className={styles.inlineInput}
                                            />
                                            {' '}
                                            days.
                                        </p>
                                    </div>
                                    <FormError error={status?.errors?.fastArchivalPeriod} />
                                </Container>

                                <h4 className="font-semibold text-[20px] text-cleanSlate">
                                    3. We&apos;ll learn from you.
                                </h4>
                                <p className="leading-normal my-2">
                                    As you move emails around, Sixty will pay attention,
                                    learning from your actions and preferences.
                                </p>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>

        </StandardModal>
    );
}

export default OnboardingFoldersModal;
